import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './styles/theme';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import About from './pages/About'; // Import the About page
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CreateMemorial from './pages/CreateMemorial';
import ViewMemorial from './pages/ViewMemorial';
import EditMemorial from './pages/EditMemorial';
import QRCodeScanner from './pages/QRCodeScanner';
import QRMemorialViewer from './pages/QRMemorialViewer';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import QRCodeGenerator from './components/admin/QRCodeGenerator';
import QRCodeList from './components/admin/QRCodeList';
import ProductList from './components/admin/ProductList';
import ErrorBoundary from './components/ErrorBoundary';
import { CircularProgress, Box } from '@mui/material';
import { Helmet } from 'react-helmet';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { auth } = useAuth();
  const location = useLocation();

  if (auth.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

// Admin Route Component - Checks for admin role
const AdminRoute = ({ children }) => {
  const { auth } = useAuth();
  const location = useLocation();

  if (auth.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Check for admin role
  if (auth.user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ErrorBoundary>
            <Router>
              <Helmet>
                <title>Digital Memorials UK | QR Codes for Gravestones & Remembrance</title>
                <meta name="description" content="Create lasting digital memorials with QR codes for gravestones. Preserve and share memories of loved ones with our premium memorial services in the UK." />
                <meta name="keywords" content="digital memorial, QR code memorial, gravestone QR code, memorial service, remember loved ones, UK memorial" />
                
                {/* Open Graph tags for social sharing */}
                <meta property="og:title" content="Digital Memorials UK | QR Codes for Gravestones" />
                <meta property="og:description" content="Create lasting digital memorials with QR codes for gravestones and remembrance." />
                <meta property="og:image" content="https://digitalmemorials.co.uk/og-image.jpg" />
                <meta property="og:url" content="https://digitalmemorials.co.uk" />
                <meta property="og:type" content="website" />
                
                {/* Twitter Card tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Digital Memorials UK | QR Codes for Gravestones" />
                <meta name="twitter:description" content="Create lasting digital memorials with QR codes for gravestones." />
                <meta name="twitter:image" content="https://digitalmemorials.co.uk/twitter-image.jpg" />
                
                {/* Canonical URL */}
                <link rel="canonical" href="https://digitalmemorials.co.uk" />
              </Helmet>
              <Navbar />
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={
                  <ErrorBoundary>
                    <Home />
                  </ErrorBoundary>
                } />
                {/* About Page Route */}
                <Route path="/about" element={
                  <ErrorBoundary>
                    <About />
                  </ErrorBoundary>
                } />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/memorial/:id" element={
                  <ErrorBoundary>
                    <ViewMemorial />
                  </ErrorBoundary>
                } />
                <Route path="/scan" element={<QRCodeScanner />} />
                <Route path="/memorial/qr/:code" element={
                  <ErrorBoundary>
                    <QRMemorialViewer />
                  </ErrorBoundary>
                } />
                {/* New Cart Route */}
                <Route path="/cart" element={
                  <ErrorBoundary>
                    <Cart />
                  </ErrorBoundary>
                } />
                <Route path="/checkout" element={
                  <ErrorBoundary>
                    <Checkout />
                  </ErrorBoundary>
                } />
                <Route path="/order-confirmation" element={
                  <ErrorBoundary>
                    <OrderConfirmation />
                  </ErrorBoundary>
                } />
                
                {/* Admin Routes - Updated to use AdminRoute component */}
                <Route path="/admin/qrcodes/generate" element={
                  <AdminRoute>
                    <QRCodeGenerator />
                  </AdminRoute>
                } />
                <Route path="/admin/qrcodes" element={
                  <AdminRoute>
                    <QRCodeList />
                  </AdminRoute>
                } />
                {/* New Product Management Route */}
                <Route path="/admin/products" element={
                  <AdminRoute>
                    <ProductList />
                  </AdminRoute>
                } />

                {/* Protected Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/create-memorial" element={
                  <ProtectedRoute>
                    <CreateMemorial />
                  </ProtectedRoute>
                } />
                <Route path="/edit-memorial/:id" element={
                  <ProtectedRoute>
                    <EditMemorial />
                  </ProtectedRoute>
                } />

                {/* 404 Route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Router>
          </ErrorBoundary>
        </ThemeProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;